import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon } from 'lucide-react';
import { DisclosureBadgeGroup } from '../Disclosure/DisclosureBadgeGroup';
import type { DisclosureId } from '../../data/disclosures';
/** Who uploaded a graphic. Scoped to graphics rather than the global role model. */
export type GraphicUploaderRole = 'reader' | 'author' | 'reviewer' | 'pr-professional' | 'business';
export const UPLOADER_ROLE_LABELS: Record<GraphicUploaderRole, string> = {
  reader: 'Reader',
  author: 'Author',
  reviewer: 'Reviewer',
  'pr-professional': 'PR Professional',
  business: 'Bookish Business'
};
export interface PromotionalGraphicProps {
  id: string;
  imageUrl: string;
  bookId: string;
  bookTitle: string;
  authorName: string;
  tags?: string[];
  description?: string;
  title?: string;
  type?: string;
  /** Reader, author, reviewer, PR professional or bookish business. */
  uploaderRole?: GraphicUploaderRole;
  uploaderName?: string;
  /**
   * Unlike reviews, graphics DECLARE organic status explicitly — an unlabelled
   * promotional image is genuinely ambiguous about whether it was commissioned.
   */
  disclosures?: DisclosureId[];
}
interface Props {
  graphic: PromotionalGraphicProps;
  showOverlay?: boolean;
  onClick?: (id: string) => void;
}
const PromotionalGraphic: React.FC<Props> = ({
  graphic,
  showOverlay = true,
  onClick
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(graphic.id);
    }
  };
  return <div className="relative rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow h-full cursor-pointer" onClick={handleClick} data-id="element-1721" data-mp="36ct4wwwwojs0">
      <div className="w-full h-full aspect-[3/4]" data-id="element-1722" data-mp="3bcxlemxtdei8">
        <img src={graphic.imageUrl} alt={`${graphic.bookTitle} - ${graphic.tags?.join(', ')}`} className="w-full h-full object-cover" data-id="element-1723" data-mp="3fe13lg3c3mmm" />
      </div>
      {showOverlay && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" data-id="element-1724" data-mp="1l0rqzffhrl05">
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white" data-id="element-1725" data-mp="2jbj8nsvh5qb6">
            <h3 className="text-lg font-semibold line-clamp-1" data-id="element-1726" data-mp="2jdnlx1f9k1o4">
              {graphic.bookTitle}
            </h3>
            <p className="text-sm text-gray-200 mb-1" data-id="element-1727" data-mp="2t9qkxyetjk6u">
              by {graphic.authorName}
              {graphic.uploaderRole && graphic.uploaderName && <span className="text-gray-300" data-id="element-1728" data-mp="377xamr9m6dvf">
                  {' '}
                  · posted by {graphic.uploaderName} (
                  {UPLOADER_ROLE_LABELS[graphic.uploaderRole]})
                </span>}
            </p>
            {/* One disclosure on the card so the image isn't crowded; the full
                set with explanations appears in the detail view. */}
            <DisclosureBadgeGroup disclosures={graphic.disclosures} subjectType="graphic" compact maxVisible={1} showIcon={false} className="mb-1" data-id="element-1729" data-mp="3q0rdimtyqfja" />
            {graphic.tags && graphic.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-1730" data-mp="dfvjciv4fbz3">
                {graphic.tags.map(tag => <span key={tag} className="text-xs px-2 py-1 bg-indigo-600/70 rounded-full" data-id="element-1731" data-mp="3vlqn5n9ze1u0">
                    {tag}
                  </span>)}
              </div>}
            <Link to={`/book/${graphic.bookId}`} className="mt-3 inline-flex items-center text-sm text-white hover:text-indigo-200" data-id="element-1732" data-mp="3qnffnj75s3ey">
              <BookOpenIcon className="h-4 w-4 mr-1" data-id="element-1733" data-mp="3gx3xosqn3tp2" />
              View Book
            </Link>
          </div>
        </div>}
    </div>;
};
export default PromotionalGraphic;